import { useState } from "react";
import { FiLock, FiEye, FiEyeOff, FiAlertCircle, FiCheck } from "react-icons/fi";
import API_URL from "./config";

export default function ResetPassword({ token, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const mismatch = confirm && password !== confirm;
  const canSubmit = password.length >= 8 && password === confirm && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (res.ok) {
        setDone(true);
      } else {
        setError(data.error || "Failed to reset password.");
      }
    } catch {
      setError("Cannot connect to server.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-orange-500/10 border border-orange-500/20 mb-4">
            <FiLock size={24} className="text-orange-400" />
          </div>
          <h1 className="text-2xl font-semibold text-white mb-1">Set new password</h1>
          <p className="text-zinc-500 text-sm">Must be at least 8 characters.</p>
        </div>

        <div className="bg-zinc-900 border border-zinc-700 rounded-3xl p-6 shadow-2xl">
          {done ? (
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-500/10 border border-green-500/20 mb-4">
                <FiCheck size={20} className="text-green-400" />
              </div>
              <p className="text-zinc-200 text-sm mb-1">Password updated.</p>
              <p className="text-zinc-500 text-xs mb-6">You can now sign in with your new password.</p>
              <button onClick={onDone}
                className="w-full py-3 bg-orange-500 hover:bg-orange-400 text-white font-semibold rounded-2xl transition-colors text-sm">
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-zinc-400 text-xs font-medium mb-1.5 uppercase tracking-wider">New password</label>
                <div className="relative">
                  <button type="button" onClick={() => setShowPass(p => !p)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors">
                    {showPass ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                  <input type={showPass ? "text" : "password"} value={password}
                    onChange={e => setPassword(e.target.value)} required autoFocus
                    className="kiosk-input" placeholder="Min. 8 characters" />
                </div>
              </div>
              <div className="mb-5">
                <label className="block text-zinc-400 text-xs font-medium mb-1.5 uppercase tracking-wider">Confirm password</label>
                <input type={showPass ? "text" : "password"} value={confirm}
                  onChange={e => setConfirm(e.target.value)} required
                  className={`kiosk-input ${mismatch ? "border-red-700" : ""}`} placeholder="Repeat password" />
                {mismatch && <p className="text-red-400 text-xs mt-1">Passwords don't match.</p>}
              </div>
              {error && (
                <div className="flex items-center gap-2 mb-4 p-3 bg-red-950/60 border border-red-800/50 rounded-xl">
                  <FiAlertCircle size={15} className="text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}
              <button type="submit" disabled={!canSubmit}
                className="w-full py-3.5 bg-orange-500 hover:bg-orange-400 text-white font-semibold rounded-2xl transition-colors disabled:opacity-40">
                {loading ? "Saving…" : "Set Password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
